"use client";

import React, { useState, useEffect } from "react";

interface TaxesInsuranceProps {
    propertyTax: number;
    setPropertyTax: (value: number) => void;
    insurance: number;
    setInsurance: (value: number) => void;
}

const formatWithCommas = (value: number | string | undefined): string =>
    typeof value === "number" || typeof value === "string"
        ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
        : "";

const TaxesInsurance: React.FC<TaxesInsuranceProps> = ({ propertyTax, setPropertyTax, insurance, setInsurance }) => {
    const [taxInput, setTaxInput] = useState("0");
    const [insuranceInput, setInsuranceInput] = useState("0");
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        setTaxInput(formatWithCommas(propertyTax));
    }, [propertyTax]);

    useEffect(() => {
        setInsuranceInput(formatWithCommas(insurance));
    }, [insurance]);

    return (
        <div className="border border-gray-300 overflow-hidden shadow 2xl:w-[900px]">
            <button
                type="button"
                className="w-full text-left bg-[#202358] text-white px-4 py-3 cursor-pointer select-none flex items-center justify-between hover:bg-blue active:bg-blue focus:bg-blue 2xl:h-[42px]"
                onClick={() => setIsOpen(!isOpen)}
            >
                <span className="text-[18px] font-bold uppercase">Taxes & Insurance</span>
                <span className="text-xl">{isOpen ? "−" : "+"}</span>
            </button>

            <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                    isOpen ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"
                }`}
            >
                <div className="bg-white p-2 2xl:w-[898px]">
                    {/* Property Tax */}
                    <div className="">
                        <div className="flex flex-col gap-3 2xl:flex 2xl:flex-row 2xl:items-start 2xl:gap-2">
                            <div className="2xl:w-[367px] 2xl:flex 2xl:justify-end">
                                <label
                                    htmlFor="propertyTax"
                                    className="w-32 font-medium text-black text-[15px] 2xl:w-[125px] content-center"
                                >
                                    Property Tax /Yr: $
                                </label>
                                <input
                                    id="propertyTax"
                                    type="text"
                                    inputMode="numeric"
                                    className="border px-3 py-1 rounded w-36 text-left 2xl:w-[122px] 2xl:h-[42px] bg-[#f6f9ff] text-[14px]"
                                    value={taxInput}
                                    onChange={(e) => {
                                        const raw = e.target.value.replace(/,/g, "");
                                        if (/^\d*$/.test(raw)) {
                                            const numeric = Number(raw);
                                            setPropertyTax(numeric);
                                            setTaxInput(formatWithCommas(raw));
                                        }
                                    }}
                                    onBlur={() => {
                                        setTaxInput(formatWithCommas(propertyTax));
                                    }}
                                />
                            </div>
                            <input
                                aria-label="Property tax per year"
                                type="range"
                                min={0}
                                max={100000}
                                step={100}
                                value={propertyTax}
                                onChange={(e) => {
                                    const val = Number(e.target.value);
                                    setPropertyTax(val);
                                    setTaxInput(formatWithCommas(val));
                                }}
                                className="w-full 2xl:w-[506px] accent-blue"
                            />
                        </div>
                        <div className="flex justify-end text-xs text-black 2xl:-mt-1">
                            <div className="pb-10 justify-between w-full max-w-md 2xl:pb-0 flex 2xl:justify-between 2xl:w-full 2xl:max-w-md 2xl:text-[10px] 2xl:-mt-3.5 2xl:mb-2.5">
                                <span>$0</span>
                                <span>$1K</span>
                                <span>$10K</span>
                                <span>$100K</span>
                            </div>
                        </div>
                    </div>

                    {/* Insurance */}
                    <div>
                        <div className="flex flex-col gap-3 2xl:flex 2xl:flex-row 2xl:items-start 2xl:gap-2">
                            <div className="2xl:w-[367px] 2xl:flex 2xl:justify-end">
                                <label
                                    htmlFor="insurance"
                                    className="w-32 font-medium text-black text-[15px] 2xl:w-[105px] content-center"
                                >
                                    Insurance /Yr: $
                                </label>
                                <input
                                    id="insurance"
                                    type="text"
                                    inputMode="numeric"
                                    className="border px-3 py-1 rounded w-36 text-left 2xl:w-[122px] 2xl:h-[42px] bg-[#f6f9ff] text-[14px]"
                                    value={insuranceInput}
                                    onChange={(e) => {
                                        const raw = e.target.value.replace(/,/g, "");
                                        if (/^\d*$/.test(raw)) {
                                            const numeric = Number(raw);
                                            setInsurance(numeric);
                                            setInsuranceInput(formatWithCommas(raw));
                                        }
                                    }}
                                    onBlur={() => {
                                        setInsuranceInput(formatWithCommas(insurance));
                                    }}
                                />
                            </div>
                            <input
                                aria-label="Homeowners insurance per year"
                                type="range"
                                min={0}
                                max={100000}
                                step={100}
                                value={insurance}
                                onChange={(e) => {
                                    const val = Number(e.target.value);
                                    setInsurance(val);
                                    setInsuranceInput(formatWithCommas(val));
                                }}
                                className="w-full 2xl:w-[506px] accent-blue"
                            />
                        </div>
                        <div className="flex justify-end text-xs text-black 2xl:-mt-1">
                            <div className="pb-10 justify-between w-full max-w-md 2xl:pb-0 flex 2xl:justify-between 2xl:w-full 2xl:max-w-md 2xl:text-[10px] 2xl:-mt-3.5 2xl:mb-2.5">
                                <span>$0</span>
                                <span>$1K</span>
                                <span>$10K</span>
                                <span>$100K</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TaxesInsurance;
